import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Save } from "lucide-react";
import { apiFetch } from "../lib/api";
import type { Role } from "../shared/schemas";
import { ErrorState, LoadingState, PageHeader } from "../components/ui";

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  role: Role;
  isActive: boolean;
  departmentCodes: string[];
}

interface AuditEvent {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  actorEmail: string | null;
  createdAt: string;
}

const emptyUser = {
  email: "",
  displayName: "",
  passcode: "",
  role: "faculty" as Role,
  departments: "",
};

function departmentCodes(value: string): string[] {
  return [...new Set(value.split(",").map((entry) => entry.trim()).filter(Boolean))];
}

export function AdminPage() {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => apiFetch<AdminUser[]>("/admin/users"),
  });
  const auditQuery = useQuery({
    queryKey: ["audit"],
    queryFn: () => apiFetch<AuditEvent[]>("/audit"),
  });
  const [newUser, setNewUser] = useState(emptyUser);
  const [drafts, setDrafts] = useState<
    Record<string, { role: Role; departments: string; active: boolean }>
  >({});
  const [passcodes, setPasscodes] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<AdminUser>("/admin/users", {
        method: "POST",
        body: JSON.stringify({
          email: newUser.email,
          displayName: newUser.displayName,
          passcode: newUser.passcode,
          role: newUser.role,
          departmentCodes: departmentCodes(newUser.departments),
        }),
      }),
    onSuccess: async () => {
      setNewUser(emptyUser);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      role,
      departments,
      active,
    }: {
      id: string;
      role: Role;
      departments: string;
      active: boolean;
    }) =>
      apiFetch(`/admin/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          role,
          isActive: active,
          departmentCodes: departmentCodes(departments),
        }),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
  });
  const passcodeMutation = useMutation({
    mutationFn: ({ id, passcode }: { id: string; passcode: string }) =>
      apiFetch(`/admin/users/${id}/passcode`, {
        method: "PATCH",
        body: JSON.stringify({ passcode }),
      }),
    onSuccess: async (_, variables) => {
      setPasscodes((current) => ({ ...current, [variables.id]: "" }));
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
  });

  function createUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createMutation.mutate();
  }

  if (usersQuery.isLoading || auditQuery.isLoading) return <LoadingState />;
  if (usersQuery.error || auditQuery.error) {
    return <ErrorState error={usersQuery.error ?? auditQuery.error} />;
  }

  const mutationError =
    createMutation.error ?? updateMutation.error ?? passcodeMutation.error;

  return (
    <>
      <PageHeader
        title="Administration"
        description="Create users, manage roles and department boundaries, and inspect audit activity."
      />
      <section className="panel p-5">
        <div className="mb-4">
          <h2 className="font-bold text-slate-900">Add User</h2>
          <p className="text-xs text-slate-500">
            Create an individual login. Passcodes must contain at least 8 characters.
          </p>
        </div>
        <form className="grid gap-3 lg:grid-cols-5" onSubmit={createUser}>
          <input
            className="field"
            type="email"
            required
            value={newUser.email}
            onChange={(event) => setNewUser((current) => ({ ...current, email: event.target.value }))}
            placeholder="Email address"
          />
          <input
            className="field"
            required
            value={newUser.displayName}
            onChange={(event) => setNewUser((current) => ({ ...current, displayName: event.target.value }))}
            placeholder="Display name"
          />
          <input
            className="field"
            type="password"
            minLength={8}
            required
            autoComplete="new-password"
            value={newUser.passcode}
            onChange={(event) => setNewUser((current) => ({ ...current, passcode: event.target.value }))}
            placeholder="Initial passcode"
          />
          <select
            className="field"
            value={newUser.role}
            onChange={(event) => setNewUser((current) => ({ ...current, role: event.target.value as Role }))}
          >
            <option value="administrator">Administrator</option>
            <option value="scheduler">Scheduler</option>
            <option value="department_chair">Department Chair</option>
            <option value="faculty">Faculty</option>
          </select>
          <div className="flex gap-2">
            <input
              className="field min-w-0 flex-1"
              value={newUser.departments}
              onChange={(event) => setNewUser((current) => ({ ...current, departments: event.target.value }))}
              placeholder="Departments: 10, 30"
            />
            <button className="button-primary px-3" type="submit" disabled={createMutation.isPending}>
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>
        </form>
        {mutationError && (
          <p className="mt-3 text-sm font-medium text-rose-700">
            {mutationError instanceof Error ? mutationError.message : "Unable to update the user."}
          </p>
        )}
      </section>
      <section className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1150px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Departments</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3">Reset Passcode</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {usersQuery.data?.map((user) => {
                const draft = drafts[user.id] ?? {
                  role: user.role,
                  departments: user.departmentCodes.join(", "),
                  active: user.isActive,
                };
                const passcode = passcodes[user.id] ?? "";
                return (
                  <tr key={user.id}>
                    <td className="px-4 py-3">
                      <p className="font-bold">{user.displayName || user.email}</p>
                      <p className="text-xs text-slate-400">{user.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        className="field"
                        value={draft.role}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [user.id]: { ...draft, role: event.target.value as Role },
                          }))
                        }
                      >
                        <option value="administrator">Administrator</option>
                        <option value="scheduler">Scheduler</option>
                        <option value="department_chair">Department Chair</option>
                        <option value="faculty">Faculty</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        className="field"
                        value={draft.departments}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [user.id]: { ...draft, departments: event.target.value },
                          }))
                        }
                        placeholder="10, 30, 70"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={draft.active}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [user.id]: { ...draft, active: event.target.checked },
                          }))
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <input
                          className="field"
                          type="password"
                          minLength={8}
                          autoComplete="new-password"
                          value={passcode}
                          onChange={(event) =>
                            setPasscodes((current) => ({
                              ...current,
                              [user.id]: event.target.value,
                            }))
                          }
                          placeholder="New passcode"
                        />
                        <button
                          className="button-secondary px-3"
                          type="button"
                          disabled={passcode.length < 8 || passcodeMutation.isPending}
                          onClick={() => passcodeMutation.mutate({ id: user.id, passcode })}
                          aria-label={`Reset passcode for ${user.email}`}
                        >
                          <KeyRound className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        className="button-primary px-3"
                        type="button"
                        onClick={() => updateMutation.mutate({ id: user.id, ...draft })}
                        aria-label={`Save ${user.email}`}
                      >
                        <Save className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="font-bold text-slate-900">Recent Audit Events</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {auditQuery.data?.slice(0, 50).map((event) => (
            <div key={event.id} className="grid gap-1 px-5 py-3 text-sm sm:grid-cols-[180px_1fr_220px]">
              <span className="font-semibold text-slate-700">{event.action}</span>
              <span className="text-slate-500">{event.entityType} - {event.entityId}</span>
              <span className="text-xs text-slate-400">{event.actorEmail ?? "system"} - {new Date(event.createdAt).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
